import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Glasses, PackageCheck } from 'lucide-react';
import {
  OpticalJobStatus,
  type OpticalJobListQuery,
  type OpticalJobStatus as JobStatus,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/utils/cn';
import { formatCount } from '@/utils/format';
import { useOpticalJobQueueQuery, useOpticalLabsListQuery } from '../api/opticalJobs.api';
import { BranchRequired } from '../components/BranchRequired';
import { JobList } from '../components/JobList';
import { trackingErrorMessage } from '../tracking/errors';

type Tab = 'open' | 'ready' | 'all';
const TABS: readonly Tab[] = ['open', 'ready', 'all'];
const PAGE_SIZE = 20;

/** The statuses a user may narrow the open list to: everything before the handover. */
const OPEN_STATUSES: readonly JobStatus[] = [
  OpticalJobStatus.ORDERED,
  OpticalJobStatus.SENT_TO_LAB,
  OpticalJobStatus.IN_LAB,
  OpticalJobStatus.RECEIVED_FROM_LAB,
  OpticalJobStatus.READY_FOR_COLLECTION,
];

/**
 * The branch's optical jobs: what is open, what is ready for the customer, and everything.
 *
 * One branch at a time, and never one chosen for the user. Filtering and paging happen on the server; the search term stays on this screen and never enters the address. Ready for collection has its own tab because it is the question asked at the counter all day — telling the customer is a person's job, not this screen's.
 */
export function OpticalJobsQueuePage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { activeBranch } = useBranch();
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.includes(params.get('tab') as Tab) ? (params.get('tab') as Tab) : 'open';

  const [search, setSearch] = useState('');
  const [settledSearch, setSettledSearch] = useState('');
  const [status, setStatus] = useState<JobStatus | ''>('');
  const [labId, setLabId] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettledSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const query: OpticalJobListQuery = {
    page,
    pageSize: PAGE_SIZE,
    ...(settledSearch ? { search: settledSearch } : {}),
    ...(labId ? { labId } : {}),
    ...(tab === 'ready'
      ? { status: OpticalJobStatus.READY_FOR_COLLECTION }
      : tab === 'open'
        ? { open: true, ...(status ? { status } : {}) }
        : status
          ? { status }
          : {}),
  };
  const jobs = useOpticalJobQueueQuery(query);
  const readyCount = useOpticalJobQueueQuery({
    status: OpticalJobStatus.READY_FOR_COLLECTION,
    pageSize: 1,
  });
  const labs = useOpticalLabsListQuery(true);

  const chooseTab = (next: Tab) => {
    setParams(next === 'open' ? {} : { tab: next }, { replace: true });
    setStatus('');
    setPage(1);
  };

  const statusOptions = (tab === 'all' ? Object.values(OpticalJobStatus) : OPEN_STATUSES).map(
    (value) => ({ value, label: t(`sales.opticalJobs.status.${value}`) }),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('opticalOrders.queue.title')}
        subtitle={t('opticalOrders.queue.subtitle')}
      />

      {!activeBranch ? (
        <BranchRequired
          message={t('opticalOrders.queue.chooseBranch')}
          label={t('opticalOrders.queue.branch')}
        />
      ) : (
        <>
          <div
            role="group"
            aria-label={t('opticalOrders.queue.views')}
            className="flex flex-wrap gap-2"
          >
            {TABS.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={tab === value}
                onClick={() => chooseTab(value)}
                className={cn(
                  'inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm',
                  'focus-visible:ring-brand-200 focus-visible:ring-2 focus-visible:outline-none',
                  tab === value
                    ? 'border-brand-600 bg-brand-50 text-brand-800 font-medium'
                    : 'border-line text-ink bg-white',
                )}
              >
                {value === 'ready' ? <PackageCheck className="size-4" aria-hidden /> : null}
                {t(`opticalOrders.queue.tabs.${value}`)}
                {value === 'ready' && readyCount.data ? (
                  <span className="bg-emerald-100 text-emerald-800 rounded-full px-2 text-xs font-medium">
                    {formatCount(readyCount.data.pagination.totalItems, locale)}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
          {tab === 'ready' ? (
            <p className="text-muted text-sm">{t('opticalOrders.queue.readyHint')}</p>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <Input
              type="search"
              label={t('opticalOrders.queue.search')}
              placeholder={t('opticalOrders.queue.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
              {t('opticalOrders.queue.status')}
              <Select
                value={tab === 'ready' ? OpticalJobStatus.READY_FOR_COLLECTION : status}
                disabled={tab === 'ready'}
                onChange={(event) => {
                  setStatus(event.target.value as JobStatus | '');
                  setPage(1);
                }}
                options={[
                  { value: '', label: t('opticalOrders.queue.anyStatus') },
                  ...statusOptions,
                ]}
              />
            </label>
            <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
              {t('opticalOrders.queue.lab')}
              <Select
                value={labId}
                onChange={(event) => {
                  setLabId(event.target.value);
                  setPage(1);
                }}
                options={[
                  { value: '', label: t('opticalOrders.queue.anyLab') },
                  ...(labs.data ?? []).map((lab) => ({
                    value: lab.id,
                    label: lab.isActive
                      ? lab.name
                      : `${lab.name} (${t('opticalOrders.labs.inactive')})`,
                  })),
                ]}
              />
            </label>
          </div>

          <Card>
            {jobs.isError ? (
              <div className="p-5">
                <Alert tone="error">{trackingErrorMessage(t, jobs.error)}</Alert>
              </div>
            ) : jobs.isPending ? (
              <LoadingState />
            ) : (jobs.data?.items.length ?? 0) === 0 ? (
              <EmptyState
                icon={tab === 'ready' ? PackageCheck : Glasses}
                title={t(`opticalOrders.queue.empty.${tab}`)}
                description={t('opticalOrders.queue.emptyHint')}
              />
            ) : (
              <>
                <JobList jobs={jobs.data?.items ?? []} />
                {jobs.data ? (
                  <Pagination
                    pagination={jobs.data.pagination}
                    onPageChange={setPage}
                    isBusy={jobs.isFetching}
                  />
                ) : null}
              </>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
